import { type UserMeTenantPartnerExEntity } from '@/libs/api'

import { buildPartnerDisplayName } from './build-partner-display-name'

export type PartnerSelectOption = {
  value: number
  label: string
}

type PartnerLike = Pick<
  UserMeTenantPartnerExEntity,
  'id' | 'name' | 'officeName' | 'dcId' | 'dc'
>

/**
 * 該当拠点 (dcId) のパートナーをパートナー ID の小さい順に並べる。
 * users/me は所属パートナーの並び順を保証しないため、代表の選び方を ID 順で固定する。
 */
const sortDcPartners = (partners: PartnerLike[], dcId: number): PartnerLike[] =>
  partners.filter((x) => x.dcId === dcId).sort((a, b) => a.id - b.id)

/**
 * 選択中の拠点 (dcId) に紐づくパートナーの表示名を組み立てる。
 * 1 ユーザーは同一拠点の複数パートナーに所属し得る (BE partner-user)。全部を並べると
 * 右上が長くなるため、パートナー ID の小さい順の先頭を代表として「パートナー名 / 事業所名」で出し、
 * 他にもあれば末尾に「 …」(半角スペース＋…) を付ける。スペースを空けないと、事業所名が幅で途中まで
 * 切れたように見えるため。全件は buildDcPartnerFullDisplayName で組み立てる。
 * 該当拠点にパートナーが無ければ空文字。
 */
export const buildDcPartnerDisplayName = (
  partners: PartnerLike[],
  dcId: number
): string => {
  const [representative, ...others] = sortDcPartners(partners, dcId)
  if (!representative) return ''
  const name = buildPartnerDisplayName(representative)
  return others.length > 0 ? `${name} …` : name
}

/**
 * 選択中の拠点 (dcId) に紐づくパートナー全件の表示名を、パートナー ID の小さい順に「、」で連結する。
 * 右上で代表 1 件に省略したときに、hover (title) で全件を見せるために使う。
 */
export const buildDcPartnerFullDisplayName = (
  partners: PartnerLike[],
  dcId: number
): string =>
  sortDcPartners(partners, dcId).map(buildPartnerDisplayName).join('、')

/**
 * パートナー切替プルダウンの選択肢を組み立てる (VOC_PJ-1107 / #6012)。
 *
 * 切替単位は拠点 (dcId / X-DC-Id) なので、所属パートナーを dcId で一意化する。
 * 1 ユーザーが同一拠点の複数パートナーに所属する所属形態があり (BE partner-user)、
 * value を partner 単位にすると dcId が重複して選択が壊れるため、拠点単位にする。
 * ラベルは右上と同じ、その拠点のパートナー表示名 (複数所属なら代表 1 件＋「 …」)。
 *
 * 同じラベルが複数の拠点に出るときだけ、ラベルに拠点名を添えて区別する (確認事項#1)。
 * 拠点名は partner.dc から取る (パートナーユーザーの UserMe では tenant.dcs は空で返り、
 * 拠点は partner.dc に入るため)。
 */
export const buildPartnerSelectOptions = (
  partners: PartnerLike[]
): PartnerSelectOption[] => {
  const dcIds = [...new Set(partners.map((x) => x.dcId))]
  const baseLabelByDc = new Map(
    dcIds.map((dcId) => [dcId, buildDcPartnerDisplayName(partners, dcId)])
  )
  const labelCounts = [...baseLabelByDc.values()].reduce<
    Record<string, number>
  >((acc, label) => {
    acc[label] = (acc[label] ?? 0) + 1
    return acc
  }, {})
  return dcIds.map((dcId) => {
    const base = baseLabelByDc.get(dcId) ?? ''
    const dcName = partners.find((x) => x.dcId === dcId)?.dc?.name
    return {
      value: dcId,
      label: labelCounts[base] > 1 && dcName ? `${base}（${dcName}）` : base
    }
  })
}
